import { useEffect } from 'react';

import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  EnablePrepaidPaymentMethodsVariation,
  defaultPrepaidPaymentMethodsVariation,
} from 'state/launchdarkly/variations';

import { usePaymentContext } from '../../state';
import { IPaymentMethod } from '../../types';
import { extractPaymentTypeForLD, isAllowedReloadMethod } from '../../utils';
import { filterExpiredCreditCards } from '../getPaymentMethodsState/utils';

export const usePrepaidsPaymentMethods = () => {
  let nonSelectedReloadPaymentMethods: IPaymentMethod[] = [];
  let reloadPaymentMethods: IPaymentMethod[] = [];
  let selectedReloadPaymentMethod: IPaymentMethod | undefined;

  const enablePrepaidPaymentMethods: EnablePrepaidPaymentMethodsVariation =
    useFlag<EnablePrepaidPaymentMethodsVariation>(
      LaunchDarklyFlag.ENABLE_PREPAID_PAYMENT_METHODS
    ) || defaultPrepaidPaymentMethodsVariation;

  const {
    canUseApplePay,
    canUseGooglePay,
    paymentMethods,
    prepaidReloadPaymentMethodId,
    setPrepaidReloadPaymentMethodId,
  } = usePaymentContext();

  if (enablePrepaidPaymentMethods.EXISTING_METHOD) {
    // Filter payment methods to only use those that are enabled for prepaid purchases/reloads.
    reloadPaymentMethods = filterExpiredCreditCards(paymentMethods).filter(paymentMethod => {
      const isValidPaymentMethod = isAllowedReloadMethod(paymentMethod);
      const paymentType = extractPaymentTypeForLD(paymentMethod);
      if (paymentType) {
        const paymentTypeIsEnabled = enablePrepaidPaymentMethods[paymentType];
        return paymentTypeIsEnabled && isValidPaymentMethod;
      }
      return isValidPaymentMethod;
    });

    // Obtain the currently selected prepaid payment method
    const storedReloadPaymentMethod = reloadPaymentMethods.find(method => {
      const accountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
      return accountIdentifier === prepaidReloadPaymentMethodId;
    });

    // Render with the first valid method immediately while the effect below synchronizes context.
    selectedReloadPaymentMethod = storedReloadPaymentMethod ?? reloadPaymentMethods[0];

    // Obtain a list of all non-selected prepaid payment methods
    nonSelectedReloadPaymentMethods = reloadPaymentMethods.filter(method => {
      const accountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
      const selectedAccountIdentifier =
        selectedReloadPaymentMethod?.accountIdentifier ?? selectedReloadPaymentMethod?.fdAccountId;
      const isNonSelectedPaymentMethod = selectedAccountIdentifier !== accountIdentifier;
      return isNonSelectedPaymentMethod;
    });
  }

  const selectedReloadPaymentMethodId =
    selectedReloadPaymentMethod?.accountIdentifier ??
    selectedReloadPaymentMethod?.fdAccountId ??
    '';

  useEffect(() => {
    if (
      enablePrepaidPaymentMethods.EXISTING_METHOD &&
      selectedReloadPaymentMethodId &&
      selectedReloadPaymentMethodId !== prepaidReloadPaymentMethodId
    ) {
      setPrepaidReloadPaymentMethodId(selectedReloadPaymentMethodId);
    }
  }, [
    enablePrepaidPaymentMethods.EXISTING_METHOD,
    prepaidReloadPaymentMethodId,
    selectedReloadPaymentMethodId,
    setPrepaidReloadPaymentMethodId,
  ]);

  const allowPrepaidApplePay = canUseApplePay && enablePrepaidPaymentMethods.APPLE_PAY;
  const allowPrepaidGooglePay = canUseGooglePay && enablePrepaidPaymentMethods.GOOGLE_PAY;

  // This is used to enable/disable using existing payment methods for prepaid reload/purchase
  const allowReloadPaymentMethodSelection =
    enablePrepaidPaymentMethods.EXISTING_METHOD &&
    (!!reloadPaymentMethods.length || allowPrepaidApplePay || allowPrepaidGooglePay);

  // This is used to enable/disable using one time payments for prepaid reload/purchase.
  const allowOneTimePaymentMethod = enablePrepaidPaymentMethods.ONE_TIME_METHOD;

  return {
    allowOneTimePaymentMethod,
    allowPrepaidApplePay,
    allowPrepaidGooglePay,
    allowReloadPaymentMethodSelection,
    nonSelectedReloadPaymentMethods,
    reloadPaymentMethods,
    selectedReloadPaymentMethod,
  };
};
